import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";
import {
  ACTIVATION,
  LIST_COMMENTAIRE,
  RECUPERE_SOUSCRIPTION,
  RECUPERER_FORFAIT,
  SOUSCRIRE,
} from "../../Utils/constant";
import ok from "../../assets/images/ok.png";
import { find, store, updateOne } from "../../services/service";
import { useStoreApp } from "../../store/storeApp";

/* ─── Étape 1 : Souscription ──────────────────────────────────────── */
const StepSouscription = ({ numeroTicket, onNext }) => {
  const [forfait, setForfait] = useState(null);
  const [souscription, setSouscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [isSubmit, setIsSubmit] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    Promise.all([
      find(`${RECUPERE_SOUSCRIPTION}?numeroTicket=${numeroTicket}`).catch(
        () => null,
      ),
      find(`${RECUPERER_FORFAIT}?numeroTicket=${numeroTicket}`).catch((e) => {
        setError(e?.response?.data);
        return null;
      }),
    ]).then(([resSouscription, resForfait]) => {
      if (resSouscription) setSouscription(resSouscription?.data?.donnee);
      if (resForfait) setForfait(resForfait?.data);
      setLoading(false);
    });
  }, []);

  const submit = (data) => {
    setIsSubmit(true);
    setMsg(null);
    store(
      `${SOUSCRIRE}/${forfait?.forfait?.id}?numeroSouscription=${data.numero}&numeroTicket=${numeroTicket}`,
      "application/json",
      {},
    )
      .then((res) => {
        if (res.status === 200) {
          setSouscription(res?.data?.donnee);
          toast.success("Souscription effectuée");
          setIsSubmit(null);
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        setMsg(err?.response?.data?.donnee);
      });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <TailSpin height="36" width="36" color="#f59e0b" visible={true} />
      </div>
    );
  }

  const isAlreadySubscribed = !!souscription;

  return (
    <div className="space-y-6">
      {/* Info forfait */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <InfoCard
          label="Forfait"
          value={
            isAlreadySubscribed
              ? souscription?.designation
              : forfait?.forfait?.designation
          }
          highlight
        />
        {!isAlreadySubscribed && (
          <>
            <InfoCard
              label="Catégorie"
              value={forfait?.forfait?.categorie
                ?.replace("_", "")
                .toLowerCase()}
            />
            <InfoCard
              label="Montant de l'équipement"
              value={forfait?.equipement?.cout}
            />
          </>
        )}
        {isAlreadySubscribed && (
          <InfoCard
            label="Description"
            value={souscription?.description}
            colSpan
          />
        )}
      </div>

      {/* Message erreur souscription */}
      {msg && <AlertBanner message={msg} />}
      {error && <AlertBanner message={error?.donnee} />}

      {/* Formulaire si pas encore souscrit */}
      {!isAlreadySubscribed && (
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Numéro FTTH <span className="text-red-400">*</span>
            </label>
            <input
              {...register("numero", { required: true })}
              type="number"
              min={0}
              defaultValue={forfait?.numeroFtth?.replace(/@mtn.ci/g, "")}
              className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
            />
            {errors.numero && (
              <span className="text-xs text-red-500 mt-1 block">
                Ce champ est obligatoire
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={!!isSubmit}
            className="w-full py-2.5 rounded-lg bg-amber-400 hover:bg-amber-500 text-black font-semibold text-sm transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmit ? (
              <>
                Veuillez patienter…
                <span className="loading loading-dots loading-xs" />
              </>
            ) : (
              "Souscrire au forfait"
            )}
          </button>
        </form>
      )}

      {/* Badge succès si déjà souscrit */}
      {isAlreadySubscribed && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          Souscription enregistrée
        </div>
      )}

      <StepFooter
        onNext={onNext}
        disabled={!isAlreadySubscribed}
        label="Terminer"
      />
    </div>
  );
};

/* ─── Étape 2 : Installation / Activation ────────────────────────── */
const StepInstallation = ({ numeroTicket, onFinish }) => {
  const [mdp, setmdp] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const commentaire = useStoreApp();
  const numeroSerie = localStorage.getItem("numeroSerie");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    commentaire.getData(LIST_COMMENTAIRE, { page: 0, param: "", size: 10 }, "");
  }, []);

  const submit = (data) => {
    setIsSubmit(true);
    updateOne(ACTIVATION, "", {
      ...data,
      status: "REALISER",
      numeroTicket,
    })
      .then((res) => {
        if (res.status === 200) {
          toast.success("Activation effectuée");
          setmdp(res?.data.donnee);
          setIsSubmit(null);
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        toast.error(err?.response?.data?.donnee);
      });
  };

  if (isSubmit === null) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <img src={ok} height={72} width={72} alt="OK" className="mx-auto" />
        <p className="text-sm text-gray-500 font-light">
          Activation de la ligne effectuée avec succès
        </p>
        <span>
          Mot de passe de l'activation : <b>{mdp}</b>
        </span>
        <button
          onClick={onFinish}
          className="mt-4 px-6 py-2 rounded-lg bg-amber-400 hover:bg-amber-500 text-black font-semibold text-sm transition"
        >
          Continuer vers la souscription
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="space-y-4 w-full lg:w-full"
    >
      {numeroSerie === "null" && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Numero de serie <span className="text-red-400">*</span>
          </label>
          <div className="flex flex-col gap-2 lg:flex-row lg:gap-1">
            <input
              {...register("numeroSerie", { required: true })}
              type="text"
              placeholder="Numéro de série l'équipement"
              className="input input-bordered text-gray-500 w-full h-10 text-sm font-semibold"
            />
          </div>
        </div>
      )}
      {/* Résultat activation */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Résultat de l'activation<span className="text-red-400">*</span>
        </label>
        <select
          {...register("resultat", { required: true })}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
        >
          <option value="" disabled selected>
            Sélectionnez un statut
          </option>
          <option value="OK">OK</option>
          <option value="NON_OK">Non OK</option>
          <option value="PARTIALY_OK">Partiellement OK</option>
        </select>
        {errors.resultat && (
          <span className="text-xs text-red-500 mt-1 block">Obligatoire</span>
        )}
      </div>

      {/* Type commentaire */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Type de commentaire <span className="text-red-400">*</span>
        </label>
        <select
          {...register("typeCommentaire", { required: true })}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition"
        >
          <option value="" disabled selected>
            Sélectionnez un type
          </option>
          {commentaire?.data?.data?.map((item) => (
            <option key={item.id} value={item.id}>
              {item.designation}
            </option>
          ))}
        </select>
        {errors.typeCommentaire && (
          <span className="text-xs text-red-500 mt-1 block">Obligatoire</span>
        )}
      </div>

      {/* Commentaire libre */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Commentaire
        </label>
        <textarea
          {...register("commentaire")}
          rows={3}
          placeholder="Votre commentaire…"
          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={!!isSubmit}
        className="w-full py-2.5 rounded-lg bg-amber-400 hover:bg-amber-500 text-black font-semibold text-sm transition disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isSubmit ? (
          <>
            Enregistrement…
            <span className="loading loading-dots loading-xs" />
          </>
        ) : (
          "Enregistrer l'installation"
        )}
      </button>
    </form>
  );
};

/* ─── Composant principal unifié ──────────────────────────────────── */
export const InstallationUnifiee = ({ numeroTicket }) => {
  // 0 = Souscription, 1 = Installation

  const steps = ["Activation", "Souscription"];
  const etape = localStorage.getItem("etape");
  const [step, setStep] = useState(
    etape === "EN_ATTENTE_DE_SOUSCRIPTION" ? 1 : 0,
  );
  const handleFinish = () => {
    window.history.back();
    localStorage.removeItem("numeroTicket");
  };

  return (
    <div className="w-full">
      {/* Header titre */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-amber-500 tracking-tight font-mtn">
          Dossier d'installation
        </h1>
        <p className="text-sm text-gray-400 mt-0.5">Ticket #{numeroTicket}</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((label, i) => (
          <div key={i} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2 flex-1">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 transition-colors ${
                  i < step
                    ? "bg-emerald-500 text-white"
                    : i === step
                      ? "bg-amber-400 text-black"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                {i < step ? (
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-sm font-medium ${
                  i === step ? "text-gray-800" : "text-gray-400"
                }`}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-px transition-colors ${
                  i < step ? "bg-emerald-400" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Contenu de l'étape */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6 ">
        {step === 0 && (
          <StepInstallation
            numeroTicket={numeroTicket}
            onFinish={() => setStep(1)}
          />
        )}
        {step === 1 && (
          <StepSouscription numeroTicket={numeroTicket} onNext={handleFinish} />
        )}
      </div>
    </div>
  );
};

/* ─── Sous-composants utilitaires ─────────────────────────────────── */

const InfoCard = ({ label, value, highlight, colSpan }) => (
  <div
    className={`rounded-xl border p-3 ${colSpan ? "sm:col-span-2" : ""} ${
      highlight ? "border-amber-200 bg-amber-50" : "border-gray-100 bg-gray-50"
    }`}
  >
    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">
      {label}
    </p>
    <p
      className={`text-sm font-semibold mt-0.5 ${
        highlight ? "text-amber-600" : "text-gray-700"
      }`}
    >
      {value ?? "--"}
    </p>
  </div>
);

const AlertBanner = ({ message }) => (
  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
    <svg
      className="w-4 h-4 text-red-500 mt-0.5 shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <p className="text-sm text-red-700">{message}</p>
  </div>
);

const StepFooter = ({ onNext, disabled, label }) => (
  <div className="pt-4 border-t border-gray-100 flex justify-end">
    <button
      onClick={onNext}
      disabled={disabled}
      className="px-5 py-2 rounded-lg bg-amber-400 hover:bg-amber-500 text-black font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  </div>
);

export default InstallationUnifiee;
